/**
 * Join funnel constants — Epic 76 Story 76.2 (T1).
 *
 * **The ONLY place the door slugs live** (AC6). Story 76.3 (intake screens)
 * and 76.4 (CTA wiring sweep) import from here rather than repeating string
 * literals — a renamed slug is then a one-file change that the route spec
 * (`join-routes.spec.ts`) and the doors spec catch.
 *
 * Panel *colours* deliberately do NOT live here: AC8 routes every colour
 * through the `@use 'variables'` SCSS tokens, so each panel carries only its
 * design-system `tone` (the scale name) and `join-panels.component.scss`
 * resolves the tone to the README §3 fallback / scrim / CTA tokens.
 */

/**
 * The six doors, in chooser order — **the single source of the slugs.**
 * `JoinDoorSlug` and `JoinPanelKey` are DERIVED from this literal, so a
 * renamed or removed slug is a compile error at every consumer (panels, the
 * hero/help links, the placeholder, 76.3/76.4) rather than a broken URL.
 */
export const JOIN_DOORS = [
  { slug: 'company', label: 'Companies', panel: 'company' },
  { slug: 'influencer', label: 'Influencers', panel: 'influencer' },
  { slug: 'artist', label: 'Artists', panel: 'artist' },
  { slug: 'organization', label: 'Organizations', panel: 'organization' },
  { slug: 'link-help', label: 'Link help', panel: null },
  { slug: 'personal', label: 'Personal accounts', panel: null },
] as const;

/** One door entry (readonly literal). */
export type JoinDoor = (typeof JOIN_DOORS)[number];

/** One of the six door slugs the chooser routes to (`/join/:door`). Derived. */
export type JoinDoorSlug = JoinDoor['slug'];

/** The four accordion panels (README §3) — the doors that have a panel. Derived. */
export type JoinPanelKey = Exclude<JoinDoor['panel'], null>;

/** Design-system colour scale a panel is painted with (see the SCSS map). */
export type JoinPanelTone = 'teal' | 'crimson' | 'navy' | 'sage';

/** Doors by slug — bind links through this (`JOIN_DOOR.company.slug`), never a string literal. */
export const JOIN_DOOR = Object.fromEntries(JOIN_DOORS.map((door) => [door.slug, door])) as {
  readonly [S in JoinDoorSlug]: Extract<JoinDoor, { slug: S }>;
};

/** Resolve a raw route segment to a door; `undefined` for anything unknown. */
export function findJoinDoor(slug: string | null | undefined): JoinDoor | undefined {
  return JOIN_DOORS.find((door) => door.slug === slug);
}

export interface JoinPanel {
  readonly key: JoinPanelKey;
  readonly slug: JoinDoorSlug;
  readonly tone: JoinPanelTone;
  readonly title: string;
  readonly description: string;
  /** Visible CTA label (D3: Companies "See if you qualify", the rest "Start"). */
  readonly cta: string;
  /** Companies only — the "Most common" tag at the panel's top-left. */
  readonly mostCommon: boolean;
}

/**
 * The four panels, README §3 order. Copy is verbatim from the README except the
 * D3 substitutions (PO ruling Q2, 2026-08-24): the Companies description's
 * leading "Apply with your company." becomes "Submit your company." — the
 * README's own text would fail AC3's forbidden-words spec ("apply").
 */
export const JOIN_PANELS: readonly JoinPanel[] = [
  {
    key: 'company',
    slug: 'company',
    tone: 'teal',
    title: 'Companies',
    description:
      'Submit your company. Registry-verified pages, direct inquiries from verified buyers.',
    cta: 'See if you qualify',
    mostCommon: true,
  },
  {
    key: 'influencer',
    slug: 'influencer',
    tone: 'crimson',
    title: 'Influencers',
    description: 'A verified collaboration profile. Briefs from vetted companies only.',
    cta: 'Start',
    mostCommon: false,
  },
  {
    key: 'artist',
    slug: 'artist',
    tone: 'navy',
    title: 'Artists',
    description: 'A page for your work and story, private until you publish.',
    cta: 'Start',
    mostCommon: false,
  },
  {
    key: 'organization',
    slug: 'organization',
    tone: 'sage',
    title: 'Organizations',
    description: 'Onboarding is a conversation with a person, not a form pipeline.',
    cta: 'Start',
    mostCommon: false,
  },
];

/** Panel keys in door order — derived from `JOIN_DOORS`, and the panels spec pins `JOIN_PANELS` to it. */
export const JOIN_PANEL_KEYS: readonly JoinPanelKey[] = JOIN_DOORS.flatMap((door) =>
  door.panel === null ? [] : [door.panel],
);

/** The panel that is open by default and that `mouseleave` resets to (README). */
export const JOIN_DEFAULT_PANEL: JoinPanelKey = 'company';

/** Nullable asset URL per panel — `null` renders the panel's solid fallback colour. */
export type JoinPanelImages = Readonly<Record<JoinPanelKey, string | null>>;

/**
 * Panel photography (AC7) — the four Unsplash photos from the design, shipped
 * same-origin under `src/assets/join/` (provenance and licences in
 * `design/join-funnel/claude-design-handoff/ASSETS-NOTE.md`; PO confirmed the
 * licensing 2026-08-27). Same-origin matters: the smoke's contrast oracle
 * samples the photo pixels through a canvas, which a cross-origin image
 * would taint. A `null` here falls back to the panel's solid tone colour.
 */
export const JOIN_PANEL_IMAGES: JoinPanelImages = {
  company: '/assets/join/panel-companies.jpg',
  influencer: '/assets/join/panel-influencers.jpg',
  artist: '/assets/join/panel-artists.jpg',
  organization: '/assets/join/panel-organizations.jpg',
};
